import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { LANGUAGES, useI18n } from '../i18n/I18nProvider';
import { MessageKey } from '../i18n/messages';
import { useTheme } from '../theme/ThemeProvider';
import { todayKey } from '../utils/date';
import { ChecklistIcon, MoonIcon, NoteIcon, SunIcon } from './icons';
import { NotePanel } from './NotePanel';

/** Ba mục này chưa có trang thật. Hiện ra để thấy hướng đi, nhưng không giả vờ bấm được. */
const PLANNED_NAV: MessageKey[] = ['nav.calendar', 'nav.stats', 'nav.help'];

export function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const signOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="app-shell">
      <Header signedIn={!!user} onSignOut={signOut} />

      {user ? (
        <div className="app-body">
          <aside className="panel side-nav" data-testid="module-nav">
            <SideTab />
          </aside>

          <main data-testid="module-content">{children}</main>
        </div>
      ) : (
        <main className="app-body-plain">{children}</main>
      )}

      <footer className="app-footer">
        <span>{t('footer.copyright', { year: new Date().getFullYear() })}</span>
        <nav className="app-footer-links">
          <a href="/terms" onClick={event => event.preventDefault()}>
            {t('footer.terms')}
          </a>
          <a href="/privacy" onClick={event => event.preventDefault()}>
            {t('footer.privacy')}
          </a>
        </nav>
      </footer>
    </div>
  );
}

function SideTab() {
  const { t } = useI18n();

  // Hiện chỉ có một phân hệ. Cột này là nơi chuyển giữa các phân hệ, không phải nơi lọc dữ liệu
  // của phân hệ đang mở — những bộ lọc đó nằm bên phải, cạnh chính dữ liệu mà chúng lọc.
  return (
    <NavLink to={`/day/${todayKey()}`} className="side-tab" data-testid="tab-todo-daily">
      <ChecklistIcon />
      {t('sidebar.todoDaily')}
    </NavLink>
  );
}

function Header({ signedIn, onSignOut }: { signedIn: boolean; onSignOut: () => void }) {
  const { user } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { theme, toggle } = useTheme();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Bấm ra ngoài thì đóng menu — nếu không, nó nằm lì trên màn hình sau khi người dùng đã chuyển
  // sự chú ý sang chỗ khác.
  useEffect(() => {
    if (!menuOpen) return;

    const close = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };

    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  const initial = (user?.displayName || user?.email || '?').trim().charAt(0).toUpperCase();

  return (
    <header className="app-header">
      <div className="app-header-inner">
        <Link className="app-brand" to={signedIn ? `/day/${todayKey()}` : '/login'}>
          {t('app.name')}
        </Link>

        {signedIn && (
          <nav className="app-nav">
            <NavLink
              to={`/day/${todayKey()}`}
              className={({ isActive }) => `app-nav-link ${isActive ? 'active' : ''}`}
            >
              {t('nav.today')}
            </NavLink>

            {PLANNED_NAV.map(key => (
              <button key={key} type="button" className="app-nav-soon" disabled title={t('nav.comingSoon')}>
                {t(key)}
              </button>
            ))}
          </nav>
        )}

        <div className="app-header-tools">
          {signedIn && (
            <button
              type="button"
              className="icon-button"
              onClick={() => setNoteOpen(true)}
              title={t('note.button')}
              aria-label={t('note.button')}
              data-testid="note-button"
            >
              <NoteIcon />
            </button>
          )}

          <button
            type="button"
            className="icon-button"
            onClick={toggle}
            title={theme === 'dark' ? t('header.toLight') : t('header.toDark')}
            aria-label={theme === 'dark' ? t('header.toLight') : t('header.toDark')}
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>

          <div className="lang-switch" role="group" aria-label={t('header.language')}>
            {LANGUAGES.map(item => (
              <button
                key={item.code}
                type="button"
                className={lang === item.code ? 'active' : ''}
                onClick={() => setLang(item.code)}
                title={item.label}
                data-testid={`lang-${item.code}`}
              >
                {item.short}
              </button>
            ))}
          </div>

          {signedIn ? (
            <div className="position-relative" ref={menuRef}>
              <button
                type="button"
                className="user-button"
                onClick={() => setMenuOpen(open => !open)}
                data-testid="user-menu"
              >
                <span className="d-none d-md-inline">{user?.displayName || user?.email}</span>
                <span className="avatar">{initial}</span>
              </button>

              {menuOpen && (
                <div className="app-menu">
                  <Link className="app-menu-item" to="/account" onClick={() => setMenuOpen(false)}>
                    {t('header.changePassword')}
                  </Link>
                  <button type="button" className="app-menu-item text-danger" onClick={onSignOut}>
                    {t('header.signOut')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link className="btn btn-sm btn-primary" to="/login">
              {t('header.signIn')}
            </Link>
          )}
        </div>
      </div>

      {noteOpen && <NotePanel onClose={() => setNoteOpen(false)} />}
    </header>
  );
}
